import type { TCustomer } from '@repo/unicity'

/** localStorage key used by `useAuthentication.restoreSession`. */
const CUSTOMER_STORAGE_KEY = 'u25_customer'

/** Generic shape returned by both the upload endpoint and `etlV2/menu`. */
export interface ProfilePicturePayload {
    sizes?: { size?: string; media?: string }[]
    href?: string
}

/** Append a fresh `?_=<stamp>` query so the browser always refetches the image. */
export const cacheBustUrl = (url: string, stamp: number): string => (url ? `${url.split('?')[0]}?_=${stamp}` : url)

/**
 * Merge the API response with the existing `profilePicture` and cache-bust every URL.
 *
 * Priority:
 * 1. Use `incoming.sizes` when present (this is a fresh upload/menu response).
 * 2. Otherwise keep the previous sizes but stamp them so the `<img>` reloads.
 */
export const buildProfilePicture = (
    incoming: ProfilePicturePayload | undefined,
    fallback: TCustomer['profilePicture'] | undefined,
    stamp: number
): TCustomer['profilePicture'] => {
    const incomingSizes = (incoming?.sizes ?? []).filter(s => Boolean(s?.media)).map(s => ({ size: s.size ?? '', media: cacheBustUrl(s.media ?? '', stamp) }))

    const sizes = incomingSizes.length > 0 ? incomingSizes : (fallback?.sizes ?? []).map(s => ({ size: s.size, media: cacheBustUrl(s.media, stamp) }))

    return {
        sizes,
        href: cacheBustUrl(incoming?.href ?? fallback?.href ?? '', stamp)
    }
}

/**
 * Mirror the customer state to localStorage so a hard refresh keeps the new
 * picture (`useAuthentication.restoreSession` reads from this key).
 */
export const persistCustomerToStorage = (customer: TCustomer): void => {
    try {
        localStorage.setItem(CUSTOMER_STORAGE_KEY, JSON.stringify(customer))
    } catch (err) {
        console.warn('Failed to persist customer to localStorage', err)
    }
}
